import { ACCOUNT_ROLES, ACCOUNT_STATES, ACCOUNT_WAITS, type AccountRole, type AccountState, type AccountWait } from "./accountsApi";
import { requestJson } from "./http/httpClient";
import { readArray, readBoolean, readObject, readOneOf, readString, type JsonObject } from "./http/jsonDecoding";
import { decodeInvitationOffer, type InvitationOffer } from "./invitationsApi";

/**
 * Account administration (administrators, `manage_accounts`): the registrations that wait for
 * a decision, and approving or rejecting them (Q-36). Routes:
 * `server/src/dmw/api/routes/accounts/users.py`.
 */

export interface Account {
  readonly userId: string;
  readonly email: string;
  readonly displayName: string;
  readonly accountRole: AccountRole;
  /** A passwordless historical actor, rather than a sign-up. */
  readonly invited: boolean;
  readonly state: AccountState;
  /** ISO 8601. */
  readonly registeredAt: string;
}

export interface PendingAccount extends Account {
  /** `approval`: the administrator's turn. */
  readonly waitsFor: AccountWait;
  /** The invitation it came from: who invited whom into what. */
  readonly invitation: InvitationOffer | null;
}

export function listPendingAccounts(signal?: AbortSignal): Promise<readonly PendingAccount[]> {
  return requestJson({
    method: "GET",
    path: "/accounts/users/pending",
    signal,
    decodeResponse: (body) => readArray(body, "pending accounts", (item) => {
      const user = readObject(item, "user");
      const invitation = user["invitation"];
      return {
        ...decodeAccount(user),
        waitsFor: readOneOf(user, "waits_for", ACCOUNT_WAITS),
        invitation: invitation === null ? null : decodeInvitationOffer(readObject(invitation, "invitation")),
      };
    }),
  });
}

/** An account in the list of all of them, with what it waits for, as the server says. */
export interface ListedAccount extends Account {
  /** `null`: it waits for nothing. */
  readonly waitsFor: AccountWait | null;
}

/** Every account of the installation (administrators). */
export function listAccounts(signal?: AbortSignal): Promise<readonly ListedAccount[]> {
  return requestJson({
    method: "GET",
    path: "/accounts/users",
    signal,
    decodeResponse: (body) => readArray(body, "accounts", (item) => {
      const user = readObject(item, "user");
      return { ...decodeAccount(user), waitsFor: user["waits_for"] === null ? null : readOneOf(user, "waits_for", ACCOUNT_WAITS) };
    }),
  });
}

export function approveAccount(userId: string): Promise<Account> {
  return decide(userId, "approve");
}

export function rejectAccount(userId: string): Promise<Account> {
  return decide(userId, "reject");
}

function decide(userId: string, decision: "approve" | "reject"): Promise<Account> {
  return requestJson({
    method: "POST",
    path: `/accounts/users/${encodeURIComponent(userId)}/${decision}`,
    decodeResponse: (body) => decodeAccount(readObject(body, "user")),
  });
}

function decodeAccount(user: JsonObject): Account {
  return {
    userId: readString(user, "id"),
    email: readString(user, "email"),
    displayName: readString(user, "display_name"),
    accountRole: readOneOf(user, "account_role", ACCOUNT_ROLES),
    invited: readBoolean(user, "invited"),
    state: readOneOf(user, "state", ACCOUNT_STATES),
    registeredAt: readString(user, "created_at"),
  };
}
